import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';

interface RoleAccess {
  [module: string]: {
    [action: string]: boolean;
  };
}

interface StaffDetails {
  _id: string;
  user_id: string;
  name: string;
  gender: string;
  email_id: string;
  parentage: string;
  dob: string;
  address: string;
  contact_no: string;
  qualification: string;
  marital_status: string;
  job_profile: string;
  account_no: string;
  bank_name: string;
  ifsc_code: string;
  joining_date: string;
  created_at: string;
  updated_at: string;
  profile_picture_url: string;
  role_access: RoleAccess | null;
}

interface ViewStaffModalProps {
  open: boolean;
  onClose: () => void;
  staffId: string | null;
}

const ACTION_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  add:        { bg: '#f0fdf4', color: '#166534', border: '#86efac' },
  edit:       { bg: '#eff6ff', color: '#1e40af', border: '#93c5fd' },
  delete:     { bg: '#fef2f2', color: '#991b1b', border: '#fca5a5' },
  view:       { bg: '#f5f3ff', color: '#5b21b6', border: '#c4b5fd' },
  activation: { bg: '#fff7ed', color: '#9a3412', border: '#fdba74' },
  approve:    { bg: '#ecfdf5', color: '#065f46', border: '#6ee7b7' },
};

const MODULE_LABELS: Record<string, { label: string; icon: string }> = {
  noticeboard:            { label: 'Noticeboard',            icon: 'pi pi-megaphone'          },
  teachers:               { label: 'Teachers',               icon: 'pi pi-users'              },
  students:               { label: 'Students',               icon: 'pi pi-graduation-cap'     },
  timetable:              { label: 'Timetable',              icon: 'pi pi-calendar'           },
  assigned_teachers:      { label: 'Assigned Teachers',      icon: 'pi pi-user-plus'          },
  events:                 { label: 'Events',                 icon: 'pi pi-star'               },
  exams_calendar:         { label: 'Exams Calendar',         icon: 'pi pi-book'               },
  reset_users_password:   { label: 'Reset User Password',    icon: 'pi pi-lock'               },
  teacher_leave_requests: { label: 'Teacher Leave Requests', icon: 'pi pi-clock'              },
  issues:                 { label: 'Issues',                 icon: 'pi pi-exclamation-circle' },
  fees_management:        { label: 'Fees Management',        icon: 'pi pi-wallet'             },
};

const formatDisplayDate = (value?: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
};

const ViewStaffModal: React.FC<ViewStaffModalProps> = ({ open, onClose, staffId }) => {
  const toast = useRef<Toast>(null);
  const [staffData, setStaffData] = useState<StaffDetails | null>(null);
  const [modules, setModules] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!open || !staffId) {
      console.log('Skipping fetch - staffId:', staffId);
      return;
    }

    const fetchAll = async () => {
      setLoading(true);
      setIsClosing(false);

      try {
        console.log('Fetching staff with ID:', staffId);

        const staffRes = await apiClient.get(`/staff/${staffId}`);

        if (staffRes.data.status === 'success') {
          setStaffData(staffRes.data.data);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: staffRes.data.message || 'Failed to fetch staff details',
            life: 5000
          });
          onClose();
          return;
        }

        try {
          const modulesRes = await apiClient.get('/role-access-modules');
          if (modulesRes.data.status === 'success') {
            setModules(modulesRes.data.modules);
          }
        } catch (moduleErr) {
          console.warn('Role access modules endpoint not found, using empty modules');
          setModules({});
        }

      } catch (error: any) {
        console.error('ViewStaffModal error:', error);
        const message = error.response?.data?.message || error.message || 'Failed to fetch staff details';
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: message,
          life: 5000
        });
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [open, staffId, onClose]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setStaffData(null);
      setLoading(true);
    }, 400);
  };

  const InfoRow = ({
    icon, label, value, isChip = false, chipColor = '#4169E1',
  }: {
    icon: string; label: string; value: string | number;
    isChip?: boolean; chipColor?: string;
  }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 0' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '36px', height: '36px', borderRadius: '10px',
        background: 'rgba(65, 105, 225, 0.08)', color: '#4169E1', flexShrink: 0,
      }}>
        <i className={icon} style={{ fontSize: '15px' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '11px', fontWeight: 700, color: '#6b7280',
          textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px',
        }}>
          {label}
        </div>
        {isChip ? (
          <span style={{
            display: 'inline-block', padding: '5px 12px', borderRadius: '8px',
            background: chipColor, color: 'white', fontSize: '13px', fontWeight: 600,
            boxShadow: `0 2px 8px ${chipColor}40`,
          }}>
            {value}
          </span>
        ) : (
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937', lineHeight: 1.3, wordBreak: 'break-word' }}>
            {value || '—'}
          </div>
        )}
      </div>
    </div>
  );

  const SectionCard = ({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) => (
    <div style={{
      background: 'white', borderRadius: '16px', padding: '16px 18px',
      border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        marginBottom: '14px', paddingBottom: '12px',
        borderBottom: '1px solid rgba(229,231,235,0.6)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '32px', height: '32px', borderRadius: '8px',
          background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)', color: 'white',
        }}>
          <i className={icon} style={{ fontSize: '15px' }} />
        </div>
        <span style={{ color: '#1e40af', fontWeight: 700, fontSize: '14px', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
          {title}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {children}
      </div>
    </div>
  );

  const RoleAccessSection = () => {
    const roleAccess = staffData?.role_access;

    const hasAnyAccess = roleAccess
      ? Object.values(roleAccess).some(actions => Object.values(actions).some(Boolean))
      : false;

    if (Object.keys(modules).length === 0) {
      return (
        <SectionCard title="Role Access" icon="pi pi-shield">
          <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px', fontWeight: 500 }}>
            <i className="pi pi-info-circle" style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }} />
            Role access modules not available
          </div>
        </SectionCard>
      );
    }

    return (
      <SectionCard title="Role Access" icon="pi pi-shield">
        {!hasAnyAccess ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px', fontWeight: 500 }}>
            <i className="pi pi-lock" style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }} />
            No access permissions assigned
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {Object.entries(modules).map(([moduleKey, actList]) => {
              const meta = MODULE_LABELS[moduleKey] ?? { label: moduleKey, icon: 'pi pi-circle' };
              const moduleAccess = roleAccess?.[moduleKey] ?? {};
              const enabledActions = actList.filter(a => moduleAccess[a]);
              if (enabledActions.length === 0) return null;

              const allOn = enabledActions.length === actList.length;

              return (
                <div key={moduleKey} style={{
                  background: '#f8fafc', borderRadius: '12px', padding: '12px 16px',
                  border: allOn ? '1px solid #86efac' : '1px solid #bfdbfe',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <div style={{
                      width: '28px', height: '28px', borderRadius: '7px',
                      background: allOn
                        ? 'linear-gradient(135deg,#059669,#34d399)'
                        : 'linear-gradient(135deg,#3b82f6,#93c5fd)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className={meta.icon} style={{ fontSize: '12px', color: 'white' }} />
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>
                      {meta.label}
                    </span>
                    {allOn && (
                      <span style={{
                        fontSize: '10px', fontWeight: 600, padding: '1px 8px', borderRadius: '20px',
                        background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
                      }}>
                        Full Access
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {actList.map(action => {
                      const on = !!moduleAccess[action];
                      const color = ACTION_COLORS[action] ?? { bg: '#f8fafc', color: '#374151', border: '#e5e7eb' };
                      return (
                        <span key={action} style={{
                          padding: '4px 12px', borderRadius: '7px',
                          border: `2px solid ${on ? color.border : '#e5e7eb'}`,
                          background: on ? color.bg : '#f9fafb',
                          color: on ? color.color : '#d1d5db',
                          fontSize: '11px', fontWeight: 600,
                          display: 'flex', alignItems: 'center', gap: '4px',
                          textTransform: 'capitalize',
                          opacity: on ? 1 : 0.5,
                        }}>
                          <i className={on ? 'pi pi-check-circle' : 'pi pi-circle'} style={{ fontSize: '10px' }} />
                          {action.replace(/_/g, ' ')}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    );
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
      <div style={{
        width: '84px', height: '84px', borderRadius: '14px',
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontSize: '44px', fontWeight: 700,
        boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
        flexShrink: 0, border: '3px solid white', overflow: 'hidden',
      }}>
        {staffData?.profile_picture_url ? (
          <img src={staffData.profile_picture_url} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <i className="pi pi-user" />
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: '4px' }}>
          {staffData?.name || 'Staff Profile'}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          {staffData?.job_profile || 'Staff Member'}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: '800px', borderRadius: '24px', overflow: 'hidden' }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white', borderBottom: '1px solid #e5e7eb',
          padding: '16px 18px', borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)',
          borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal dismissableMask draggable={false} resizable={false} blockScroll
      >
        <style>{`
          .p-dialog-mask { backdrop-filter: blur(4px); background-color: rgba(0,0,0,0.25) !important; animation: maskFadeIn 0.35s ease; }
          .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
          @keyframes maskFadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes maskFadeOut { from { opacity: 1; } to { opacity: 0; } }
          .p-dialog { animation: dialogOpen 0.35s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25) !important; }
          .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4,0,0.2,1) !important; }
          @keyframes dialogOpen { from { opacity: 0; transform: scale(0.92) translateY(-20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          @keyframes dialogClose { from { opacity: 1; transform: scale(1) translateY(0); } to { opacity: 0; transform: scale(0.88) translateY(25px); } }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(241,245,249,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100,116,139,0.7); }
        `}</style>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', gap: '16px' }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 600 }}>Loading staff details...</span>
          </div>
        ) : staffData ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            <SectionCard title="Personal Information" icon="pi pi-user">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '6px' }}>
                <InfoRow icon="pi pi-user" label="Full Name" value={staffData.name} />
                <InfoRow icon="pi pi-briefcase" label="Job Profile" value={staffData.job_profile} />
                <InfoRow icon="pi pi-users" label="Gender" value={staffData.gender} />
                <InfoRow icon="pi pi-user" label="Parentage" value={staffData.parentage} />
                <InfoRow icon="pi pi-calendar" label="Date of Birth" value={formatDisplayDate(staffData.dob)} />
                <InfoRow icon="pi pi-graduation-cap" label="Qualification" value={staffData.qualification} />
                <InfoRow icon="pi pi-heart" label="Marital Status" value={staffData.marital_status} />
                <InfoRow icon="pi pi-calendar-plus" label="Joining Date" value={formatDisplayDate(staffData.joining_date)} />
              </div>
            </SectionCard>

            <SectionCard title="Contact Information" icon="pi pi-phone">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '6px' }}>
                <InfoRow icon="pi pi-envelope" label="Email Address" value={staffData.email_id} />
                <InfoRow icon="pi pi-phone" label="Contact Number" value={staffData.contact_no} />
                <InfoRow icon="pi pi-map-marker" label="Address" value={staffData.address} />
              </div>
            </SectionCard>

            <SectionCard title="Bank Information" icon="pi pi-building">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '6px' }}>
                <InfoRow icon="pi pi-credit-card" label="Account Number" value={staffData.account_no} />
                <InfoRow icon="pi pi-building" label="Bank Name" value={staffData.bank_name} />
                <InfoRow icon="pi pi-id-card" label="IFSC Code" value={staffData.ifsc_code} />
              </div>
            </SectionCard>

            <RoleAccessSection />

          </div>
        ) : null}
      </Dialog>
    </>
  );
};

export default ViewStaffModal;